import { useCallback, useEffect, useState } from "react";
import { Instagram, Loader2, MapPin, Pencil, Phone, Plus, Sparkles, Store, Trash2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, dataBR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { EmptyState, Field, Loading, Surface } from "@/components/ds";
import { NativeSelect } from "@/components/ds/NativeSelect";
import { ResponsiveModal } from "@/components/ds/ResponsiveModal";

export const PROSPECT_STATUS = {
  NOVO: "Novo",
  CONTATADO: "Contatado",
  INTERESSADO: "Interessado",
  VIROU_PARCEIRO: "Virou parceiro",
  SEM_INTERESSE: "Sem interesse",
};

const ORIGINS = {
  INTERESSE_PROPRIO: { label: "Interesse próprio", dot: "bg-emerald-400", cls: "border-emerald-500/40 bg-emerald-500/15 text-emerald-300" },
  PROSPECCAO_TR3ND: { label: "Prospecção TR3ND", dot: "bg-sky-400", cls: "border-sky-500/40 bg-sky-500/15 text-sky-300" },
};

const statusCls = (s) =>
  ({
    NOVO: "border-amber-500/40 bg-amber-500/15 text-amber-300",
    CONTATADO: "border-sky-500/40 bg-sky-500/15 text-sky-300",
    INTERESSADO: "border-brand/40 bg-brand/15 text-brand",
    VIROU_PARCEIRO: "border-emerald-500/40 bg-emerald-500/15 text-emerald-300",
    SEM_INTERESSE: "border-white/15 bg-secondary/60 text-muted-foreground",
  }[s] || "border-white/15 bg-secondary/60 text-muted-foreground");

const EMPTY = {
  nome_comercio: "",
  responsavel: "",
  whatsapp: "",
  cidade: "",
  estado: "",
  instagram: "",
  observacoes: "",
};

export function ProspectsPanel({ onConvert }) {
  const [list, setList] = useState(null);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [filter, setFilter] = useState("TODOS");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/prospects");
      setList(data);
    } catch (e) {
      toast.error(apiError(e));
      setList([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (id, status) => {
    try {
      await api.put(`/prospects/${id}/status`, { status });
      toast.success("Status atualizado.");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/prospects/${toDelete.id}`);
      toast.success("Prospecção excluída.");
      setToDelete(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
      setToDelete(null);
    }
  };

  if (list === null) return <Loading label="Carregando prospecções..." />;

  const visible = filter === "TODOS" ? list : list.filter((p) => p.origem === filter);

  return (
    <div className="space-y-6" data-testid="prospects-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {[
            ["TODOS", `Todas (${list.length})`],
            ["INTERESSE_PROPRIO", `Interesse próprio (${list.filter((p) => p.origem === "INTERESSE_PROPRIO").length})`],
            ["PROSPECCAO_TR3ND", `Prospecção TR3ND (${list.filter((p) => p.origem === "PROSPECCAO_TR3ND").length})`],
          ].map(([key, label]) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              data-testid={`prospect-filter-${key.toLowerCase()}`}
              className={`tr-chip ${filter === key ? "tr-chip-active" : ""}`}
            >
              {label}
            </button>
          ))}
        </div>
        <Button
          onClick={() => setEditing("new")}
          data-testid="new-prospect-btn"
          className="h-11 gap-2 rounded-full px-5 font-semibold transition-transform active:scale-95"
        >
          <Plus className="h-4 w-4" /> Nova prospecção
        </Button>
      </div>

      {visible.length === 0 ? (
        <EmptyState
          icon={Sparkles}
          title="Nenhuma prospecção por aqui"
          description="Cadastre uma loja que você quer prospectar ou compartilhe a página pública /sejaparceiro no seu catálogo."
          action={
            <Button onClick={() => setEditing("new")} data-testid="empty-new-prospect-btn" className="gap-2 rounded-full">
              <Plus className="h-4 w-4" /> Nova prospecção
            </Button>
          }
        />
      ) : (
        <div className="grid gap-4 stagger md:grid-cols-2 xl:grid-cols-3">
          {visible.map((p) => {
            const origin = ORIGINS[p.origem] || ORIGINS.PROSPECCAO_TR3ND;
            return (
              <Surface key={p.id} className="min-w-0 overflow-hidden p-5" data-testid={`prospect-card-${p.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate font-display text-base font-medium" data-testid={`prospect-name-${p.id}`}>
                      {p.nome_comercio}
                    </p>
                    {p.responsavel && (
                      <p className="truncate text-xs text-muted-foreground">{p.responsavel}</p>
                    )}
                  </div>
                  <span
                    className={`shrink-0 rounded-full border px-2.5 py-1 text-xs ${statusCls(p.status)}`}
                    data-testid={`prospect-status-${p.id}`}
                  >
                    {PROSPECT_STATUS[p.status] || p.status}
                  </span>
                </div>

                <span
                  className={`mt-3 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs ${origin.cls}`}
                  data-testid={`prospect-origin-${p.id}`}
                >
                  <span className={`h-1.5 w-1.5 rounded-full ${origin.dot}`} /> {origin.label}
                </span>

                <div className="mt-4 space-y-1.5 text-xs text-muted-foreground">
                  {p.whatsapp && (
                    <p className="flex items-center gap-1.5">
                      <Phone className="h-3.5 w-3.5" /> <span className="font-num">{p.whatsapp}</span>
                    </p>
                  )}
                  {(p.cidade || p.estado) && (
                    <p className="flex items-center gap-1.5">
                      <MapPin className="h-3.5 w-3.5" /> {[p.cidade, p.estado].filter(Boolean).join(" / ")}
                    </p>
                  )}
                  {p.instagram && (
                    <p className="flex items-center gap-1.5 truncate">
                      <Instagram className="h-3.5 w-3.5" /> {p.instagram}
                    </p>
                  )}
                  <p className="flex items-center gap-1.5">
                    <Store className="h-3.5 w-3.5" /> Criada em {dataBR(p.created_at)}
                  </p>
                </div>

                {p.observacoes && (
                  <p className="mt-3 line-clamp-3 rounded-lg border border-white/10 bg-secondary/40 p-3 text-xs text-muted-foreground">
                    {p.observacoes}
                  </p>
                )}

                <div className="mt-4 border-t border-white/10 pt-3">
                  <NativeSelect
                    value={p.status}
                    onChange={(v) => setStatus(p.id, v)}
                    options={Object.entries(PROSPECT_STATUS).map(([k, label]) => ({
                      value: k,
                      label,
                    }))}
                    data-testid={`prospect-status-select-${p.id}`}
                  />

                  <div className="mt-3 flex flex-wrap justify-end gap-1">
                    {p.status !== "VIROU_PARCEIRO" && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onConvert(p)}
                        data-testid={`prospect-convert-btn-${p.id}`}
                        className="gap-1.5 text-xs text-brand hover:text-brand"
                      >
                        <UserPlus className="h-3.5 w-3.5" /> Converter em parceiro
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditing(p)}
                      data-testid={`prospect-edit-btn-${p.id}`}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setToDelete(p)}
                      data-testid={`prospect-delete-btn-${p.id}`}
                      className="text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </Surface>
            );
          })}
        </div>
      )}

      {editing && (
        <ProspectModal
          prospect={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={load}
        />
      )}

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="border-white/10 bg-card" data-testid="delete-prospect-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir prospecção?</AlertDialogTitle>
            <AlertDialogDescription>
              O registro de <strong>{toDelete?.nome_comercio}</strong> será removido da lista de
              prospecções. Parceiros já cadastrados não são afetados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-prospect-btn">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              data-testid="confirm-delete-prospect-btn"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ProspectModal({ prospect, onClose, onSaved }) {
  const [form, setForm] = useState(prospect ? { ...EMPTY, ...prospect } : EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = {
      nome_comercio: form.nome_comercio,
      responsavel: form.responsavel || "",
      whatsapp: form.whatsapp || "",
      cidade: form.cidade || "",
      estado: form.estado || "",
      instagram: form.instagram || "",
      observacoes: form.observacoes || "",
    };
    try {
      if (prospect) await api.put(`/prospects/${prospect.id}`, body);
      else await api.post("/prospects", body);
      toast.success(prospect ? "Prospecção atualizada." : "Prospecção cadastrada.");
      onClose();
      onSaved();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ResponsiveModal
      open
      onOpenChange={(o) => !o && onClose()}
      title={prospect ? "Editar prospecção" : "Nova prospecção"}
      description={
        prospect
          ? "Atualize os dados do contato comercial."
          : "Cadastro manual: a origem fica como Prospecção TR3ND e o status inicia em Novo."
      }
      testid="prospect-modal"
    >
      <form onSubmit={submit} className="mt-2 flex flex-col gap-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Nome da loja / comércio">
            <Input
              value={form.nome_comercio}
              onChange={set("nome_comercio")}
              required
              placeholder="Ex.: Loja Presentes & Cia"
              data-testid="prospect-store-input"
            />
          </Field>
          <Field label="Nome do responsável">
            <Input value={form.responsavel} onChange={set("responsavel")} data-testid="prospect-owner-input" />
          </Field>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="WhatsApp" hint="Ex.: +5551999999999">
            <Input value={form.whatsapp} onChange={set("whatsapp")} className="font-num" data-testid="prospect-phone-input" />
          </Field>
          <Field label="Instagram">
            <Input value={form.instagram} onChange={set("instagram")} placeholder="@sualoja" data-testid="prospect-instagram-input" />
          </Field>
        </div>
        <div className="grid gap-4 sm:grid-cols-[1fr_120px]">
          <Field label="Cidade">
            <Input value={form.cidade} onChange={set("cidade")} data-testid="prospect-city-input" />
          </Field>
          <Field label="Estado">
            <Input
              value={form.estado}
              onChange={(e) => setForm((f) => ({ ...f, estado: e.target.value.toUpperCase().slice(0, 2) }))}
              maxLength={2}
              placeholder="RS"
              data-testid="prospect-state-input"
            />
          </Field>
        </div>
        <Field label="Observações">
          <Textarea value={form.observacoes} onChange={set("observacoes")} rows={3} data-testid="prospect-notes-input" />
        </Field>
        <Button
          type="submit"
          disabled={busy}
          data-testid="save-prospect-btn"
          className="h-12 rounded-full font-semibold transition-transform active:scale-95"
        >
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Salvar prospecção
        </Button>
      </form>
    </ResponsiveModal>
  );
}
